// Storage Service - Wrapper for workflow persistence
// Now using IndexedDB for unlimited storage capacity

import indexedDBAdapter from './indexedDBAdapter.js';
import storageMigration from '../utils/storageMigration.js';

const STORAGE_KEY = 'bible_workflows';
const UI_PREFERENCES_KEY = 'bible_ui_preferences';
const ENHANCEMENT_SETTINGS_KEY = 'bible_enhancement_settings';

class StorageService {
  constructor() {
    this.initialized = false;
    this.initPromise = null;
  }

  // Initialize storage and handle migration
  async init() {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._performInit();
    await this.initPromise;
    this.initialized = true;
  }

  async _performInit() {
    try {
      // Initialize IndexedDB
      await indexedDBAdapter.init();

      // Check if migration is needed and perform it
      if (!storageMigration.hasMigrationCompleted()) {
        // console.log('🔄 Performing storage migration to IndexedDB...');
        const migrationResult = await storageMigration.performMigration();

        if (!migrationResult.success) {
          console.warn('⚠️ Migration failed, falling back to localStorage');
          // Could implement fallback to localStorage here if needed
        } else {
          // console.log('✅ Storage migration completed successfully');
        }
      }
    } catch (error) {
      console.error('❌ Storage initialization failed:', error);
    }
  }

  // Get all saved workflows
  async getAllWorkflows() {
    try {
      await this.init();
      return await indexedDBAdapter.getWorkflows();
    } catch (error) {
      console.error('Error getting workflows:', error);
      // Fallback to localStorage if IndexedDB fails
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    }
  }

  // Save a workflow
  async saveWorkflow(name, workflowData) {
    try {
      await this.init();
      const workflows = await this.getAllWorkflows();

      // Check for duplicate name
      if (workflows.some(w => w.name === name)) {
        throw new Error('A workflow with this name already exists');
      }

      // Create workflow object
      const workflow = {
        id: `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name,
        timestamp: Date.now(),
        createdAt: new Date().toISOString(),
        ...workflowData
      };

      // Add to workflows array
      workflows.push(workflow);

      // Save to IndexedDB
      await indexedDBAdapter.setWorkflows(workflows);

      return workflow;
    } catch (error) {
      console.error('Error saving workflow:', error);
      throw error;
    }
  }

  // Load a specific workflow by name
  async loadWorkflow(name) {
    try {
      const workflows = await this.getAllWorkflows();
      const workflow = workflows.find(w => w.name === name);

      if (!workflow) {
        throw new Error('Workflow not found');
      }

      return workflow;
    } catch (error) {
      console.error('Error loading workflow:', error);
      throw error;
    }
  }

  // Check if workflow name exists
  async workflowExists(name) {
    try {
      const workflows = await this.getAllWorkflows();
      return workflows.some(w => w.name === name);
    } catch (error) {
      console.error('Error checking workflow:', error);
      return false;
    }
  }

  // Delete a workflow
  async deleteWorkflow(name) {
    try {
      await this.init();
      const workflows = await this.getAllWorkflows();
      const filtered = workflows.filter(w => w.name !== name);
      await indexedDBAdapter.setWorkflows(filtered);
      return true;
    } catch (error) {
      console.error('Error deleting workflow:', error);
      return false;
    }
  }

  // Update an existing workflow
  async updateWorkflow(name, workflowData) {
    try {
      await this.init();
      const workflows = await this.getAllWorkflows();
      const index = workflows.findIndex(w => w.name === name);

      if (index === -1) {
        throw new Error('Workflow not found');
      }

      // Update workflow
      workflows[index] = {
        ...workflows[index],
        ...workflowData,
        updatedAt: new Date().toISOString()
      };

      await indexedDBAdapter.setWorkflows(workflows);
      return workflows[index];
    } catch (error) {
      console.error('Error updating workflow:', error);
      throw error;
    }
  }

  // UI Preferences methods
  async getUIPreferences() {
    try {
      await this.init();
      const data = await indexedDBAdapter.getUIPreferences();
      return data ? JSON.parse(data) : {
        isMinimapVisible: true,
        searchOptions: {
          sortBy: 'recent',
          viewMode: 'list'
        }
      };
    } catch (error) {
      console.error('Error getting UI preferences:', error);
      return {
        isMinimapVisible: true,
        searchOptions: {
          sortBy: 'recent',
          viewMode: 'list'
        }
      };
    }
  }

  async saveUIPreferences(preferences) {
    try {
      await this.init();
      const currentPrefs = await this.getUIPreferences();
      const updatedPrefs = { ...currentPrefs, ...preferences };
      await indexedDBAdapter.setUIPreferences(updatedPrefs);
      return updatedPrefs;
    } catch (error) {
      console.error('Error saving UI preferences:', error);
      throw error;
    }
  }

  async updateMinimapVisibility(isVisible) {
    try {
      const preferences = await this.getUIPreferences();
      preferences.isMinimapVisible = isVisible;
      return await this.saveUIPreferences(preferences);
    } catch (error) {
      console.error('Error updating minimap visibility:', error);
      throw error;
    }
  }

  async updateSearchOptions(searchOptions) {
    try {
      const preferences = await this.getUIPreferences();
      preferences.searchOptions = { ...preferences.searchOptions, ...searchOptions };
      return await this.saveUIPreferences(preferences);
    } catch (error) {
      console.error('Error updating search options:', error);
      throw error;
    }
  }

  // Enhancement Settings methods
  async getEnhancementSettings() {
    try {
      await this.init();
      const data = await indexedDBAdapter.getEnhancementSettings();
      return data ? JSON.parse(data) : {
        backgroundType: 'gradient3', // Default Plasma gradient
        customColor: '#ff6b6b',
        padding: {
          top: 100,
          right: 100,
          bottom: 100,
          left: 100,
          uniform: true,
          horizontalLocked: false,
          verticalLocked: false
        },
        borderRadius: 50,
        lastTextSettings: {
          fontSize: 100,
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          color: '#000000',
          bold: false,
          italic: false,
          underline: false,
          shadow: false,
          lastTextValue: '',
          lastTextX: null,
          lastTextY: null
        },
        logoSizePercentage: 10,
        logoPosition: { x: 20, y: 20 }
      };
    } catch (error) {
      console.error('Error getting enhancement settings:', error);
      return {
        backgroundType: 'gradient3',
        customColor: '#ff6b6b',
        padding: {
          top: 100,
          right: 100,
          bottom: 100,
          left: 100,
          uniform: true,
          horizontalLocked: false,
          verticalLocked: false
        },
        borderRadius: 50,
        lastTextSettings: {
          fontSize: 100,
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          color: '#000000',
          bold: false,
          italic: false,
          underline: false,
          shadow: false,
          lastTextValue: '',
          lastTextX: null,
          lastTextY: null
        },
        logoSizePercentage: 10,
        logoPosition: { x: 20, y: 20 }
      };
    }
  }

  async saveEnhancementSettings(settings) {
    try {
      await this.init();
      const currentSettings = await this.getEnhancementSettings();
      const updatedSettings = { ...currentSettings, ...settings };
      await indexedDBAdapter.setEnhancementSettings(updatedSettings);
      return updatedSettings;
    } catch (error) {
      console.error('Error saving enhancement settings:', error);
      throw error;
    }
  }

  async updateEnhancementBackground(backgroundType, customColor = null) {
    try {
      const settings = await this.getEnhancementSettings();
      settings.backgroundType = backgroundType;
      if (customColor) {
        settings.customColor = customColor;
      }
      return await this.saveEnhancementSettings(settings);
    } catch (error) {
      console.error('Error updating background:', error);
      throw error;
    }
  }

  async updateEnhancementPadding(padding) {
    try {
      const settings = await this.getEnhancementSettings();
      settings.padding = padding;
      return await this.saveEnhancementSettings(settings);
    } catch (error) {
      console.error('Error updating padding:', error);
      throw error;
    }
  }

  async updateEnhancementBorderRadius(borderRadius) {
    try {
      const settings = await this.getEnhancementSettings();
      settings.borderRadius = borderRadius;
      return await this.saveEnhancementSettings(settings);
    } catch (error) {
      console.error('Error updating border radius:', error);
      throw error;
    }
  }

  async updateEnhancementTextSettings(textSettings) {
    try {
      const settings = await this.getEnhancementSettings();
      settings.lastTextSettings = { ...settings.lastTextSettings, ...textSettings };
      return await this.saveEnhancementSettings(settings);
    } catch (error) {
      console.error('Error updating text settings:', error);
      throw error;
    }
  }

  async updateEnhancementLogoSettings(logoSettings) {
    try {
      const settings = await this.getEnhancementSettings();
      if (logoSettings.sizePercentage !== undefined) {
        settings.logoSizePercentage = logoSettings.sizePercentage;
      }
      if (logoSettings.position !== undefined) {
        settings.logoPosition = logoSettings.position;
      }
      return await this.saveEnhancementSettings(settings);
    } catch (error) {
      console.error('Error updating logo settings:', error);
      throw error;
    }
  }
}

const storageService = new StorageService();
export default storageService;